import { loadMessages, locale as localeDevExtreme } from "devextreme/localization";
import ruMessages from "devextreme/localization/messages/ru.json";
import { addLocale, locale } from "primereact/api";
import { Fragment, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { activityWatcher } from "../features/auth/activityWatcherForSession";
import ru from '../features/ruLocalePrimeReact/ru.json';
import { getItemWebStorage } from "../features/webStorageSaver";
import { RoadsListPage } from "../pages";
import { AnalyticsObjectsChoosePage } from "../pages/analyticsObjectsChoosePage/AnalyticsObjectsChoosePage";
import { AnalyticsPage } from "../pages/analyticsPage";
import { AnalyticsReportPage } from "../pages/analyticsReportPage";
import CustomObjectsOnMapInfo from "../pages/customObjectsOnMapInfo/CustomObjectsOnMapInfo";
import DevExtremeObjectsTablePage from "../pages/devExtremeObjectsTablePage/DevExtremeObjectsTablePage";
import { DiagnosticsMeasureResultsTablePage } from "../pages/diagnostics/diagnosticsMeasureResultsSection";
import { DiagnosticsPathNormativitiesSectionPage } from "../pages/diagnostics/diagnosticsPathNormativitiesSection";
import { LastYearsMapFilter } from "../pages/lastYearsMapFilter";
import { MapGeoMarkers, MapHighwayNetworkSegments, MapObject, MapObjectType, MapPath, MapPathNormativitiesMeasureType, MapPathNormativitiesMeasureTypesGroup, MapRoadAccidents, MapRoadAccidentSegments, MapRoadBedLayerSegments, MapRoadCategorySegments, MapRoadDiagnosticsSegments, MapRoadWebCameras, MapRoadWorkSegmentsPODD, MapTransportHistory, MapTransportMonitoring, MapWeather, CustomMapObjectType } from '../pages/mapPages';
import { ObjectInfoCard, RosreestrObjectInfoCard, CustomObjectInfoCard, FederalRoadInfoCard, RegionInfoCard, RoadInfoCard } from '../pages/cards';
import { MapSettings } from "../pages/mapSettings/MapSettings";
import MissingPage from "../pages/missingPage/MissingPage";
import { PanoramaFiltersPage } from "../pages/panorama/panoramaFiltersPage";
import PanoramaPage from "../pages/panorama/panoramaPage/PanoramaPage";
import RoadWebCameraPage from "../pages/roadWebCameraPage/RoadWebCameraPage";
import CustomNestedTable from "../pages/tables/custom/customNestedTable/CustomNestedTable";
import CustomTableDocuments from "../pages/tables/custom/customTableDocuments/CustomTableDocuments";
import CustomTableObjectType from "../pages/tables/custom/customTableObjectType/CustomTableObjectType";
import TableObjectType from "../pages/tables/tableObjectType/TableObjectType";
import { YearFilterPage } from "../pages/yearFilterPage";
import TableRosreestrObjects from '../pages/tables/tableRosreestrObjects/TableRosreestrObjects';
import MapRoadDiagnosticsSegmentsPassport from '../pages/mapPages/additional/mapRoadDiagnosticsSegmentsPassport/MapRoadDiagnosticsSegmentsPassport';
import TableDocuments from '../pages/tables/tableDocuments/TableDocuments';
import DocumentViewerPage from '../pages/documents/documentViewerPage/DocumentViewerPage';
import CustomDocumentViewerPage from '../pages/documents/customDocumentViewerPage/CustomDocumentViewerPage';
import TableRoadDiagnosticsSegments from '../pages/tables/diagnostics/tableRoadDiagnosticsSegments/TableRoadDiagnosticsSegments';
import RoadDiagnosticsSegmentComposition from '../pages/tables/diagnostics/roadDiagnosticsSegmentComposition/RoadDiagnosticsSegmentComposition';
import { MapGismeteoPrecipitation } from '../pages/mapPages/mapGismeteo';
import TableNestedRoadMeasureResults from '../pages/tables/diagnostics/tableNestedRoadMeasureResults/TableNestedRoadMeasureResults';
import TableNestedDefects from '../pages/tables/diagnostics/tableNestedDefects/TableNestedDefects';
import ObjectsOnMapPointInfo from "../pages/objectsOnMapPointInfo/ObjectsOnMapPointInfo";
import { MapWaysideLandLots } from "../pages/mapPages/additional/mapWaysideLandLots";
import { useGlobalKey } from "../hooks/utils/useGlobalKey";
import { useFavicon } from "../hooks/utils/useFavicon";
import { escapeBack } from "../features/keyboardShortcuts/escapeBack";
import LandLotComposition from "../pages/tables/landtLots/LandLotComposition/LandLotComposition";
import TableLandLotEncumbrances from "../pages/tables/landtLots/TableLandLotEncumbrances";
import TableLandLotOwners from "../pages/tables/landtLots/tableLandLotOwners/TableLandLotOwners";
import OverallAnalyticsPage from "../pages/analytics/overallAnalyticsPage/OverallAnalyticsPage";
import RoadWeatherStationPage from "../pages/roadWeatherStationPage/RoadWeatherStationPage";
import { WeatherStationReviewPage } from "../pages/weatherStationReviewPage";
import { MapWeatherStations } from "../pages/mapPages/additional/mapWeatherStations";
import { MapRepairPlan } from "../pages/mapPages/additional/mapRepairPlan";
import TransportInfoCard from "src/pages/cards/transportInfoCard/TransportInfoCard";
import HelpPage from "src/pages/help/HelpPage";
import { MapRoadWorksWithNormativities } from "src/pages/mapPages/additional/mapRoadWorksWithNormativities";
import SupportTicketListPage from "src/pages/help/supportTicketList/SupportTicketListPage";
import SupportTicketPage from "src/pages/help/supportTicket/SupportTicketPage";
import { useTitle } from "src/hooks/utils/useTitle";
import { getConfigMapDefinitions, renderConfigMapRoutes } from "src/features/configPages";

// Главный компонент сайта, отображаемый после успешной авторизации
function AfterLoginApp() {
    const navigate = useNavigate();
    const location = useLocation()

    useGlobalKey('Escape', () => escapeBack(location))

    useEffect(() => {
        const persist = getItemWebStorage("persist")
        if (!persist) {
            activityWatcher(navigate);
        }
    }, [])

    addLocale('ru', ru.ru);
    locale('ru');
    localeDevExtreme(navigator.language)
    loadMessages(ruMessages)

    const currentConfig = getItemWebStorage('currentConfig')
    const isRoadsListPageAvailable = currentConfig?.menuShow?.road;
    const { mapAdditionalItems, tableAdditionalItems, cardAdditionalItems, restServerType } = currentConfig ?? {}
    const configMapDefinitions = getConfigMapDefinitions(currentConfig)
    useFavicon(restServerType)
    useTitle(restServerType)

    return (
        <Routes>
            {/* Дороги */}
            {isRoadsListPageAvailable &&
                <>
                    <Route path="roads/list" element={<RoadsListPage />} />
                    <Route path="road/:roadId" element={<RoadInfoCard />} />
                    <Route path="road/:roadId/documents" element={<TableDocuments />} />
                    <Route path="road/:roadId/documents/:documentId" element={<DocumentViewerPage />} />
                </>
            }
            {/* Таблица и карточка объекта*/}
            <Route path="table/:objectTypeName" element={<TableObjectType />} />
            <Route path="tableDevExtreme/:objectTypeName" element={<DevExtremeObjectsTablePage />} />
            <Route path="table/:objectTypeName/:objectId" element={<ObjectInfoCard />} />
            <Route path="table/:objectTypeName/:objectId/documents" element={<TableDocuments />} />
            <Route path="table/:objectTypeName/:objectId/documents/:documentId" element={<DocumentViewerPage />} />
            <Route path="table/RoadLandLots/:landLotId/composition" element={<LandLotComposition />} />
            <Route path="table/RoadLandLots/:landLotId/encumbrances" element={<TableLandLotEncumbrances />} />
            <Route path="table/RoadLandLots/:landLotId/owners" element={<TableLandLotOwners />} />
            <Route path="table/RoadLandLots/:landLotId/owners/documents/:landRightDocumentId" element={<DocumentViewerPage />} />
            <Route path="table/AdjacentLandLots/:landLotId/composition" element={<LandLotComposition />} />
            <Route path="table/AdjacentLandLots/:landLotId/encumbrances" element={<TableLandLotEncumbrances />} />
            <Route path="table/AdjacentLandLots/:landLotId/owners" element={<TableLandLotOwners />} />
            <Route path="table/AdjacentLandLots/:landLotId/owners/documents/:landRightDocumentId" element={<DocumentViewerPage />} />
            {tableAdditionalItems && tableAdditionalItems.map((item: any, idx: number) => (
                <Fragment key={idx}>
                    {item?.available && <Route key={`table-${idx}`} path={item?.url} element={<CustomTableObjectType />} />}
                    {item?.nestedTable?.available && <Route key={`nested-${idx}`} path={item?.nestedTable?.url} element={<CustomNestedTable />} />}
                </Fragment>
            ))}
            {cardAdditionalItems && cardAdditionalItems.map((item: any, idx: number) => (
                <Fragment key={idx}>
                    {item?.available && <Route key={`card-${idx}`} path={item?.url} element={<CustomObjectInfoCard />} />}
                    {item?.documents?.available && <Route key={`card-docs-${idx}`} path={item?.documents?.url} element={<CustomTableDocuments />} />}
                    {item?.documents?.documentViewer?.available && <Route key={`card-doc-view-${idx}`} path={item?.documents?.documentViewer?.url} element={<CustomDocumentViewerPage />} />}
                </Fragment>
            ))}
            {/* Объекты Росреестра */}
            <Route path="rosreestrObjects/:categoryId/:subcategoryId" element={<TableRosreestrObjects />} />
            <Route path="rosreestrObjects/:categoryId/:subcategoryId/:objectId" element={<RosreestrObjectInfoCard />} />

            {/* Справка */}
            <Route path="help" element={<HelpPage />} />
            <Route path="help/supportTickets" element={<SupportTicketListPage />} />
            <Route path="help/supportTickets/:ticketId" element={<SupportTicketPage />} />
            {/* Карта */}
            {renderConfigMapRoutes(configMapDefinitions)}
            <Route path="map">
                {/* Дополнительные карты из конфига*/}
                <Route path="RoadWorkSegmentsPODD" element={<MapRoadWorkSegmentsPODD />} />
                <Route path="RoadDiagnosticsSegmentsPassport" element={<MapRoadDiagnosticsSegmentsPassport />} />
                <Route path="RoadWorksWithNormativities" element={<MapRoadWorksWithNormativities />} />
                <Route path="WaysideLandLots" element={<MapWaysideLandLots />} />
                <Route path="WeatherStations" element={<MapWeatherStations />} />
                <Route path="RepairPlan" element={<MapRepairPlan />} />

                {/* Специальные карты для существующих типов объектов */}
                <Route path="HighwayNetworkSegments" element={<MapHighwayNetworkSegments />} />
                <Route path="RoadBedLayerSegments" element={<MapRoadBedLayerSegments />} />
                <Route path="RoadCategorySegments" element={<MapRoadCategorySegments />} />
                <Route path="RoadDiagnosticsSegments" element={<MapRoadDiagnosticsSegments />} />
                <Route path="RoadDiagnosticsSegments/filters" element={<LastYearsMapFilter filterType="page" numberOfYears={5} objectType="RoadDiagnosticsSegments" />} />
                <Route path="PathNormativities/:measureType" element={<MapPathNormativitiesMeasureType />} />
                <Route path="PathNormativities/filters" element={<YearFilterPage />} />
                <Route path="PathNormativities/group" element={<MapPathNormativitiesMeasureTypesGroup />} />
                <Route path="PathNormativities/group/filters" element={<YearFilterPage />} />
                <Route path="RoadAccidents" element={<MapRoadAccidents />} />
                <Route path="RoadAccidentSegments" element={<MapRoadAccidentSegments />} />
                <Route path="RoadAccidentSegments/filters" element={<YearFilterPage />} />
                <Route path="GeoMarkers" element={<MapGeoMarkers />} />

                {/* Базовые карты */}
                <Route path=":objectTypeName" element={<MapObjectType />} />
                <Route path=":objectTypeName/:objectId" element={<MapObject />} />
                <Route path="path/:pathId" element={<MapPath />} />
                <Route path="transport/now" element={<MapTransportMonitoring />} />
                <Route path="transport/history" element={<MapTransportHistory />} />
                <Route path="gismeteo/precipitation" element={<MapGismeteoPrecipitation />} />
            </Route>
            {/* Карты интеграции */}
            {mapAdditionalItems && mapAdditionalItems.map((item: any, idx: number) => (
                <Fragment key={idx}>
                    {item?.available && <Route key={`map-${idx}`} path={item?.url} element={<CustomMapObjectType />} />}
                    {item?.objectsList?.available && <Route key={`map-list-${idx}`} path={item?.objectsList?.url} element={<CustomObjectsOnMapInfo />} />}
                </Fragment>
            ))}

            {/* Настройки слоев карты и список объектов в точке */}
            <Route path="mapSettings/:mapType" element={<MapSettings />} />
            <Route path="objectsList" element={<ObjectsOnMapPointInfo />} />
            {/* Погода */}
            <Route path="weather" element={<MapWeather />} />
            <Route path="weather/:weatherStationId" element={<RoadWeatherStationPage />} />
            <Route path="weatherStations/:stationId" element={<WeatherStationReviewPage />} />
            {/* Камеры */}
            <Route path="cameras" element={<MapRoadWebCameras />} />
            <Route path="cameras/:roadWebCameraId" element={<RoadWebCameraPage />} />
            {/* Карточка транспортного средства */}
            <Route path="transport/:cardType/:transportId" element={<TransportInfoCard />} />
            {/* Карточки федеральной дороги и региона */}
            <Route path="osmFederalRoad/:roadId" element={<FederalRoadInfoCard />} />
            <Route path="osmRegion/:regionId" element={<RegionInfoCard />} />
            {/* Аналитика */}
            <Route path="overallAnalytics" element={<OverallAnalyticsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="analytics/:objectTypeName" element={<AnalyticsPage />} />
            <Route path="analytics/selectObjects" element={<AnalyticsObjectsChoosePage />} />
            <Route path="analyticsReport" element={<AnalyticsReportPage />} />
            {/* Диагностика */}
            <Route path="diagnostics/pathNormativities" element={<DiagnosticsPathNormativitiesSectionPage />} />
            <Route path="diagnostics/pathNormativities/filters" element={<YearFilterPage />} />
            <Route path="diagnostics/diagnosticsSegments" element={<TableRoadDiagnosticsSegments />} />
            <Route path="diagnostics/diagnosticsSegments/:eventId/composition" element={<RoadDiagnosticsSegmentComposition />} />
            <Route path="diagnostics/diagnosticsSegments/:eventId/roadMeasures/:measureId/roadMeasureResults/:measureTypeId" element={<TableNestedRoadMeasureResults />} />
            <Route path="diagnostics/diagnosticsSegments/:eventId/defects/:defectClassificator" element={<TableNestedDefects />} />
            <Route path="diagnostics/diagnosticsMeasureResults/:measureType" element={<DiagnosticsMeasureResultsTablePage />} />
            <Route path="diagnostics/diagnosticsMeasureResults/filters" element={<YearFilterPage />} />
            {/* Панорама */}
            <Route path="panorama" element={<PanoramaPage />} />
            <Route path="panoramaFilters" element={<PanoramaFiltersPage />} />
            {/* Прочее */}
            <Route path="error" element={<MissingPage />} />
            <Route path="*" element={<MissingPage />} />
        </Routes>
    );
}
export default AfterLoginApp;
